/**
 * Catalog context, a clinic's Photos (H59 part 2). Pure.
 *
 * Two checks live here. `checkPhotoFile` is the browser's, run before an
 * upload, and mirrors the bucket's own limits so the Manager reads the screen's
 * sentence rather than a storage error (the same stance as `logoFile.ts`).
 * `parsePhotoUrls` is the command's: whatever a client sends, a photo can only
 * be an object the clinic uploaded into its own folder of the photos bucket.
 */
import { failure, type Failure } from "@/modules/shared/failure";

export const PHOTO_BUCKET = "business-photos";
export const PHOTO_MAX_COUNT = 10;
export const PHOTO_MAX_BYTES = 5 * 1024 * 1024;

const PHOTO_TYPES: Record<string, string> = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/webp": "webp",
};

export type PhotoCheck =
  | { ok: true; contentType: string; extension: string }
  | { ok: false; error: string };

export function checkPhotoFile(file: { type: string; size: number }): PhotoCheck {
  const extension = PHOTO_TYPES[file.type];
  if (!extension || file.size === 0) {
    return { ok: false, error: "Use PNG, JPEG or WebP photos." };
  }
  if (file.size > PHOTO_MAX_BYTES) {
    return { ok: false, error: "Keep each photo under 5 MB." };
  }
  return { ok: true, contentType: file.type, extension };
}

/** The sentence under the grid when more were picked than fit. */
export const PHOTOS_TOO_MANY = "A clinic can show up to 10 photos.";

/**
 * The public address prefix of a clinic's photo folder, e.g.
 * `https://<ref>.supabase.co/storage/v1/object/public/business-photos/<id>/`.
 */
export function photoFolderUrl(supabaseUrl: string, businessId: string): string {
  const base = supabaseUrl.replace(/\/+$/, "");
  return `${base}/storage/v1/object/public/${PHOTO_BUCKET}/${businessId}/`;
}

/**
 * Validate the ordered photo list a save carries.
 *
 * Each entry must be a string under the clinic's own folder with an object
 * name after it and no way out of the folder (`..`). Duplicates are refused,
 * since one photo twice is a client bug rather than a choice. Absent means an
 * empty list: a clinic may show no photos. A null folder (a host with no
 * storage configured) admits no photo at all.
 */
export function parsePhotoUrls(
  raw: unknown,
  folderUrl: string | null,
): { ok: true; urls: string[] } | { ok: false; failure: Failure } {
  if (raw == null) return { ok: true, urls: [] };
  if (!Array.isArray(raw)) return { ok: false, failure: failure("photo_url_invalid") };
  if (raw.length > PHOTO_MAX_COUNT) {
    return { ok: false, failure: failure("photos_too_many") };
  }
  const urls: string[] = [];
  for (const entry of raw) {
    if (typeof entry !== "string" || !isInFolder(entry, folderUrl) || urls.includes(entry)) {
      return { ok: false, failure: failure("photo_url_invalid") };
    }
    urls.push(entry);
  }
  return { ok: true, urls };
}

function isInFolder(url: string, folderUrl: string | null): boolean {
  if (folderUrl === null || !url.startsWith(folderUrl)) return false;
  const rest = url.slice(folderUrl.length);
  if (rest === "" || rest.length > 300) return false;
  const path = rest.split(/[?#]/, 1)[0];
  return path !== "" && !path.split("/").some((part) => part === "" || part === "..");
}
